/**
 * Audit-log query + read schemas (F0.9). Zod is the single source of validation
 * (CLAUDE.md): `AuditLogQueryInput` validates the platform-console request to the
 * `tenant-audit-log` edge function on BOTH sides; `AuditLogEntry` is the row shape
 * the viewers render (actor already resolved to a display name server-side).
 */

import { z } from 'zod';
import { AUDIT_ACTIONS, AUDITED_ENTITY_TYPES } from '../enums/index.js';
import { uuid, timestamptz, jsonObject } from './common.js';

/** Pagination bounds for a single audit-log page read. */
export const AUDIT_LOG_PAGE_DEFAULT = 50;
export const AUDIT_LOG_PAGE_MAX = 100;

/**
 * Query for one page of a tenant's audit log. `tenantId` is REQUIRED here (unlike
 * the tenant-scoped client tables) because the console is a separate auth realm with
 * no tenant grant — the target tenant is an explicit, server-verified parameter.
 */
export const AuditLogQueryInput = z
  .object({
    tenantId: uuid,
    limit: z
      .number()
      .int()
      .positive()
      .max(AUDIT_LOG_PAGE_MAX)
      .default(AUDIT_LOG_PAGE_DEFAULT),
    offset: z.number().int().nonnegative().default(0),
    action: z.enum(AUDIT_ACTIONS).optional(),
    entityType: z.enum(AUDITED_ENTITY_TYPES).optional(),
  })
  .strict();
export type AuditLogQueryInput = z.infer<typeof AuditLogQueryInput>;

/** How the acting party is surfaced to the viewer. */
export const AUDIT_ACTOR_KINDS = ['user', 'support', 'system'] as const;
export type AuditActorKind = (typeof AUDIT_ACTOR_KINDS)[number];

/**
 * One audit-log entry as returned to a viewer. `action` / `entity_type` stay plain
 * strings (not the enums) so an entry written by a future module still renders — the
 * UI falls back to the raw value when it has no label.
 */
export const AuditLogEntry = z.object({
  id: uuid,
  created_at: timestamptz,
  action: z.string(),
  entity_type: z.string(),
  entity_id: uuid.nullable(),
  actor_kind: z.enum(AUDIT_ACTOR_KINDS),
  actor_name: z.string().nullable(),
  is_support_access: z.boolean(),
  reason: z.string().nullable(),
  prior_value: jsonObject.nullable(),
  posterior_value: jsonObject.nullable(),
});
export type AuditLogEntry = z.infer<typeof AuditLogEntry>;

/** Response envelope from the tenant-audit-log edge function. */
export const AuditLogPage = z.object({
  entries: z.array(AuditLogEntry),
  hasMore: z.boolean(),
});
export type AuditLogPage = z.infer<typeof AuditLogPage>;
